/**
 * MobileBottomBar — fixed action bar at the bottom of the screen on mobile.
 *
 * Buttons: Offres | Adapter | Mise en page | Exporter | Plus
 * "Mise en page" opens a compact popup with template/margins/engine options.
 */

import { memo, useState, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import {
  Briefcase,
  WandSparkles,
  Save,
  Palette,
  MoreHorizontal,
  Mail,
  Globe,
  ChartNoAxesCombined,
  Zap,
  LayoutDashboard,
  FileInput,
  X,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import type { SimpleModalType } from '../SimpleMode/SimpleActionRail';
import type { getTType } from '../../contexts/I18nContext';
import type { LayoutTemplate, PreviewEngine } from '../../hooks/useEditorUi';
import type { MarginSize } from '../panels/VisualPanel';
import { useModal } from '../../contexts/ModalContext';
import { useNotif, type NotifAction } from '../../contexts/NotifContext';

interface MobileBottomBarProps {
  t: getTType;
  onSwitchToPreview: () => void;
  // Mise en page state
  layoutTemplate: LayoutTemplate;
  setLayoutTemplate: (v: LayoutTemplate) => void;
  marginSize: MarginSize;
  setMarginSize: (v: MarginSize) => void;
  previewEngine: PreviewEngine;
  setPreviewEngine: (v: PreviewEngine) => void;
}

export const MobileBottomBar = memo(function MobileBottomBar({
  t,
  onSwitchToPreview,
  layoutTemplate,
  setLayoutTemplate,
  marginSize,
  setMarginSize,
  previewEngine,
  setPreviewEngine,
}: MobileBottomBarProps) {
  const navigate = useNavigate();
  const { activeModal, setActiveModal } = useModal();
  const { pending, clear, registerTarget } = useNotif();
  const [moreOpen, setMoreOpen] = useState(false);
  const [layoutOpen, setLayoutOpen] = useState(false);

  // True when at least one notifiable action is pending — drives the dot on
  // the "Plus" button (since letter/translate/score live inside that sheet).
  const hasPlusNotif = useMemo(
    () => pending.letter || pending.translate || pending.score,
    [pending],
  );
  const hasAdaptNotif = pending.adapt;

  const handleAction = useCallback(
    (id: Exclude<SimpleModalType, null>) => {
      // Opening the corresponding modal consumes the notification.
      if (id === 'letter' || id === 'translate' || id === 'score' || id === 'adapt') {
        clear(id as NotifAction);
      }
      setActiveModal(activeModal === id ? null : id);
      setMoreOpen(false);
      setLayoutOpen(false);
    },
    [activeModal, setActiveModal, clear],
  );

  const TEMPLATES: Array<{ id: LayoutTemplate; label: string; color: string }> = [
    { id: 'ats', label: 'ATS', color: 'bg-green-600' },
    { id: 'moderne', label: 'Moderne', color: 'bg-blue-600' },
    { id: 'classique', label: 'Classique', color: 'bg-blue-600' },
    { id: 'pro', label: 'Pro', color: 'bg-purple-600' },
  ];

  const MARGINS: Array<{ id: MarginSize; label: string }> = [
    { id: 'none', label: 'Aucune' },
    { id: 'small', label: 'Petite' },
    { id: 'medium', label: 'Moyenne' },
    { id: 'large', label: 'Grande' },
  ];

  const MORE_ACTIONS: Array<{
    id: string;
    icon: React.ElementType;
    label: string;
    color: string;
    action: () => void;
  }> = [
    { id: 'letter', icon: Mail, label: t('actionRail.letter'), color: 'text-violet-400', action: () => handleAction('letter') },
    { id: 'translate', icon: Globe, label: t('actionRail.translate'), color: 'text-cyan-400', action: () => handleAction('translate') },
    { id: 'score', icon: ChartNoAxesCombined, label: t('actionRail.scoreAts'), color: 'text-emerald-400', action: () => handleAction('score') },
    { id: 'assistant', icon: Zap, label: t('actionRail.assistant'), color: 'text-amber-400', action: () => { setMoreOpen(false); navigate('/wizard'); } },
    { id: 'dashboard', icon: LayoutDashboard, label: t('actionRail.applications'), color: 'text-white/70', action: () => { setMoreOpen(false); navigate('/dashboard'); } },
    { id: 'import', icon: FileInput, label: t('actionRail.import'), color: 'text-teal-400', action: () => { setMoreOpen(false); navigate('/wizard?mode=import'); } },
  ];

  return (
    <>
      {/* Bottom bar */}
      <div className="mobile-bottom-bar fixed bottom-0 left-0 right-0 flex items-center justify-around bg-[var(--app-bg,#0a0a0a)] border-t border-white/10 px-0.5 py-1 pb-[max(0.25rem,env(safe-area-inset-bottom))]">
        {/* Offres */}
        <button type="button" onClick={() => handleAction('offers')}
          className={`flex flex-col items-center gap-0.5 flex-1 py-1 rounded-lg transition-colors touch-manipulation min-h-[44px] text-blue-400 ${activeModal === 'offers' ? 'bg-blue-500/20' : 'bg-transparent hover:bg-blue-500/10'}`}>
          <Briefcase className="size-5" />
          <span className="text-[9px] font-semibold leading-none">{t('actionRail.offers')}</span>
        </button>
        {/* Adapter */}
        <button
          type="button"
          ref={(el) => registerTarget('adapt', el)}
          onClick={() => handleAction('adapt')}
          className={`relative flex flex-col items-center gap-0.5 flex-1 py-1 rounded-lg transition-colors touch-manipulation min-h-[44px] text-indigo-400 ${activeModal === 'adapt' ? 'bg-indigo-500/20' : 'bg-transparent hover:bg-indigo-500/10'}`}>
          <WandSparkles className="size-5" />
          <span className="text-[9px] font-semibold leading-none">{t('actionRail.adapt')}</span>
          {hasAdaptNotif && (
            <span className="absolute top-0.5 right-[22%] flex h-2.5 w-2.5 items-center justify-center">
              <span className="absolute inline-flex h-full w-full rounded-full bg-rose-500 opacity-75 animate-ping" />
              <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-rose-500 ring-2 ring-[var(--app-bg,#0a0a0a)]" />
            </span>
          )}
        </button>
        {/* Mise en page */}
        <button type="button" onClick={() => { setLayoutOpen((v) => !v); setMoreOpen(false); setActiveModal(null); onSwitchToPreview(); }}
          className={`flex flex-col items-center gap-0.5 flex-1 py-1 rounded-lg transition-colors touch-manipulation min-h-[44px] text-purple-400 ${layoutOpen ? 'bg-purple-500/20' : 'bg-transparent hover:bg-purple-500/10'}`}>
          <Palette className="size-5" />
          <span className="text-[9px] font-semibold leading-none">{t('actionRail.layout')}</span>
        </button>
        {/* Exporter */}
        <button type="button" onClick={() => handleAction('export')}
          className={`flex flex-col items-center gap-0.5 flex-1 py-1 rounded-lg transition-colors touch-manipulation min-h-[44px] text-orange-400 ${activeModal === 'export' ? 'bg-orange-500/20' : 'bg-transparent hover:bg-orange-500/10'}`}>
          <Save className="size-5" />
          <span className="text-[9px] font-semibold leading-none">{t('actionRail.export')}</span>
        </button>
        {/* Plus — letter lives inside this sheet on mobile, so register it
            as the target for the 'letter' flight and show a combined dot
            when any of letter / translate / score has a pending notification. */}
        <button
          type="button"
          ref={(el) => { registerTarget('letter', el); registerTarget('translate', el); registerTarget('score', el); }}
          onClick={() => { setMoreOpen((v) => !v); setLayoutOpen(false); setActiveModal(null); }}
          className={`relative flex flex-col items-center gap-0.5 flex-1 py-1 rounded-lg transition-colors touch-manipulation min-h-[44px] text-white/70 ${moreOpen ? 'bg-white/10' : 'bg-transparent hover:bg-white/5'}`}>
          <MoreHorizontal className="size-5" />
          <span className="text-[9px] font-semibold leading-none">{t('nav.plus')}</span>
          {hasPlusNotif && (
            <span className="absolute top-0.5 right-[22%] flex h-2.5 w-2.5 items-center justify-center">
              <span className="absolute inline-flex h-full w-full rounded-full bg-rose-500 opacity-75 animate-ping" />
              <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-rose-500 ring-2 ring-[var(--app-bg,#0a0a0a)]" />
            </span>
          )}
        </button>
      </div>

      {/* ── Mise en page popup — compact, doesn't obstruct CV ── */}
      {layoutOpen &&
        createPortal(
          <div className="fixed inset-0 z-[9996]" onClick={() => setLayoutOpen(false)}>
            <div
              className="absolute bottom-[calc(3.75rem+env(safe-area-inset-bottom,0px))] left-2 right-2 bg-[var(--app-bg,#0a0a0a)] border border-white/15 rounded-2xl p-3 shadow-2xl shadow-black/60 animate-in slide-in-from-bottom-4 duration-150"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Template selector */}
              <div className="mb-3">
                <div className="text-[10px] font-bold uppercase text-white/40 tracking-wider mb-1.5">Template</div>
                <div className="flex gap-1">
                  {TEMPLATES.map(({ id, label, color }) => (
                    <button key={id} type="button" onClick={() => setLayoutTemplate(id)}
                      className={`flex-1 py-1.5 text-xs font-bold rounded-lg transition-all ${layoutTemplate === id ? `${color} text-white` : 'text-white/40 bg-white/5'}`}>
                      {label}
                    </button>
                  ))}
                </div>
              </div>
              {/* Margins */}
              <div className="mb-3">
                <div className="text-[10px] font-bold uppercase text-white/40 tracking-wider mb-1.5">Marges</div>
                <div className="flex gap-1">
                  {MARGINS.map(({ id, label }) => (
                    <button key={id} type="button" onClick={() => setMarginSize(id)}
                      className={`flex-1 py-1.5 text-xs font-bold rounded-lg transition-all ${marginSize === id ? 'bg-purple-600 text-white' : 'text-white/40 bg-white/5'}`}>
                      {label}
                    </button>
                  ))}
                </div>
              </div>
              {/* Engine */}
              <div>
                <div className="text-[10px] font-bold uppercase text-white/40 tracking-wider mb-1.5">Moteur</div>
                <div className="flex gap-1">
                  <button type="button" onClick={() => setPreviewEngine('react')}
                    className={`flex-1 py-1.5 text-xs font-bold rounded-lg transition-all ${previewEngine === 'react' ? 'bg-amber-600 text-white' : 'text-white/40 bg-white/5'}`}>
                    React
                  </button>
                  <button type="button" onClick={() => setPreviewEngine('typst')}
                    className={`flex-1 py-1.5 text-xs font-bold rounded-lg transition-all ${previewEngine === 'typst' ? 'bg-amber-600 text-white' : 'text-white/40 bg-white/5'}`}>
                    Typst
                  </button>
                </div>
              </div>
            </div>
          </div>,
          document.body,
        )}

      {/* ── More sheet overlay ── */}
      {moreOpen &&
        createPortal(
          <div className="fixed inset-0 z-[9997] flex flex-col justify-end" onClick={() => setMoreOpen(false)}>
            <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
            <div
              className="relative bg-[var(--app-bg,#0a0a0a)] border-t border-white/15 rounded-t-2xl px-4 pt-4 pb-[calc(4.5rem+env(safe-area-inset-bottom,0px))] animate-in slide-in-from-bottom duration-200"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-bold text-white/80 uppercase tracking-wider">Actions</h3>
                <button type="button" onClick={() => setMoreOpen(false)}
                  className="p-1.5 rounded-full text-white/50 hover:text-white hover:bg-white/10 transition-colors">
                  <X className="size-5" />
                </button>
              </div>
              <div className="grid grid-cols-3 gap-2">
                {MORE_ACTIONS.map(({ id, icon: Icon, label, color, action }) => {
                  const isNotifAction = id === 'letter' || id === 'translate' || id === 'score';
                  const hasNotif = isNotifAction && pending[id as NotifAction];
                  return (
                  <button key={id} type="button" onClick={action}
                    className={`relative flex flex-col items-center gap-1.5 py-3 px-1 rounded-xl border border-white/10 hover:bg-white/5 transition-colors touch-manipulation min-h-[44px] ${color}`}>
                    <Icon className="size-5" />
                    <span className="text-[10px] font-semibold leading-tight text-center">{label}</span>
                    {hasNotif && (
                      <span className="absolute -top-1 -right-1 flex h-3 w-3 items-center justify-center">
                        <span className="absolute inline-flex h-full w-full rounded-full bg-rose-500 opacity-75 animate-ping" />
                        <span className="relative inline-flex h-3 w-3 rounded-full bg-rose-500 ring-2 ring-[var(--app-bg,#0a0a0a)]" />
                      </span>
                    )}
                  </button>
                  );
                })}
              </div>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
});
